import { useContext, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'

import { AuthorizationContext } from '../contexts'
import { IconButtonSelect } from './IconButtonSelect.component'
import { MenuItem } from './MenuItem.component'
import { LANGUAGE_TO_NAME } from '../translates'
import { LANGUAGES } from '../translates/languages.constant'
import { HorizontalDivider } from './HorizontalDivider.component'
import { CHAT_WITH_MANAGER_LINK_ENV } from '../envs'
import { DEFAULT_ADMIN_ROUTE, DEFAULT_INVESTOR_ROUTE, ROUTE } from '../routing'
import { Button } from './Button.component'
import { classNames } from '../utils'
import { CabinetIcon, ChatTgIcon, LogInIcon, LogOutIcon, RefreshIcon } from '../icons'
import { refreshAllPortfoliosRequest } from '../services'


const LANG_CODE_NAME_PATCHES = {
  'uk': 'ua'
}

const StyledRoot = styled.header`
  z-index: ${({ theme }) => theme.zIndex.header};
  position: sticky;
  position: -webkit-sticky;
  top: 0;

  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  grid-column-gap: 10px;
  padding: 20px 30px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};

  background: ${({ theme }) => theme.color.backgroundPrimary};

  &.home {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.screenWidth.mobile}) {
    padding: 0 20px;
  }
`

const StyledLogoWrapper = styled.div`
  line-height: 0;

  @media (max-width: ${({ theme }) => theme.screenWidth.mobile}) {
    svg {
      max-width: 175px;
    }
  }
`

const StyledActions = styled.div`
  --margin-left: 30px;

  display: flex;

  & > * {
    margin-left: var(--margin-left)
  }

  & > *:first-child {
    margin-left: 0;
  }

  @media (max-width: ${({ theme }) => theme.screenWidth.mobile}) {
    --margin-left: 16px;
  }
`

const StyledAuthorizeButton = styled(NavLink)`
  text-transform: uppercase;
`

const StyledRefreshButton = styled(Button)`
  text-transform: uppercase;
`

const StyledDesktopOnlyElement = styled.div`
  @media (max-width: ${({ theme }) => theme.screenWidth.mobile}) {
    display: none;
  }
`

const StyledMobileOnlyElement = styled.div`
  @media (min-width: ${({ theme }) => theme.screenWidth.mobileMinWidth}) {
    display: none;
  }
`

export const Header = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()

  const { isAuthorized, isAdmin, logout } = useContext(AuthorizationContext)

  const [isRefreshingAllInvestorsData, setIsRefreshingAllInvestorsData] = useState(false)

  const isHome = location.pathname === ROUTE.home
  const isLogin = location.pathname.startsWith(ROUTE.login)

  const refreshAllInvestorsData = () => {
    setIsRefreshingAllInvestorsData(true)

    refreshAllPortfoliosRequest()
      .then(() => {
        window.location.reload()
      })
      .finally(() => {
        setIsRefreshingAllInvestorsData(false)
      })
  }

  return (
    <StyledRoot className={classNames({
      'home': isHome,
    })}>
      <StyledLogoWrapper>
        <NavLink to={isAuthorized ? isAdmin ? DEFAULT_ADMIN_ROUTE : DEFAULT_INVESTOR_ROUTE : ROUTE.home}>
          <svg width="301" height="71" viewBox="0 0 301 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M113 52C113 48.7411 113 35.212 113 31.986C113 28.7272 113 25.4848 113 22.2589C113 21.5676 113.049 20.9751 113.147 20.4813C113.277 19.9875 113.588 19.6254 114.078 19.395C114.6 19.1317 115.45 19 116.626 19H124.173V49C124.173 50.6569 122.83 52 121.173 52H113Z"
              fill="url(#paint0_linear_144_55)"/>
            <path
              d="M130.525 47.0398V27.6348C130.525 26.3181 130.737 25.4458 131.162 25.0179C131.619 24.557 132.649 24.3266 134.249 24.3266H144.982L152.823 35.6832L153.166 37.6089V24.3266H161.252C161.186 24.3924 161.137 24.6558 161.105 25.1166C161.072 25.5445 161.056 26.0383 161.056 26.5979C161.056 27.1246 161.056 27.5854 161.056 27.9804V44.2747C161.056 45.5256 160.729 46.2991 160.076 46.5954C159.455 46.8917 158.344 47.0398 156.743 47.0398H147.04L138.807 35.1894L138.464 33.2637V47.0398H130.525Z"
              fill="url(#paint1_linear_144_55)"/>
            <path
              d="M180.954 47.0398C179.288 47.0398 178.03 46.7929 177.18 46.2991C176.364 45.8054 175.612 44.8343 174.926 43.3859L166.007 24.3266H178.307L183.551 36.6707C183.649 36.9011 183.747 37.0822 183.845 37.2139C183.943 37.3455 184.041 37.4114 184.139 37.4114H184.286L190.02 24.3266H198.253L187.619 47.0398H180.954Z"
              fill="url(#paint2_linear_144_55)"/>
            <path
              d="M202.617 47.0398V27.6348C202.617 26.2523 202.863 25.3635 203.353 24.9685C203.843 24.5405 204.806 24.3266 206.244 24.3266H231.188V30.0543H213.791V32.77H229.473V38.4483H213.791V41.3121H231.188V47.0398H202.617Z"
              fill="url(#paint3_linear_144_55)"/>
            <path
              d="M236.355 40.2258C237.27 40.555 238.413 40.8019 239.786 40.9665C241.19 41.0981 242.644 41.1804 244.147 41.2134C245.65 41.2463 246.989 41.2627 248.166 41.2627C248.623 41.2627 249.211 41.2627 249.93 41.2627C250.681 41.2298 251.269 41.2134 251.694 41.2134C252.282 41.2134 252.74 41.164 253.066 41.0652C253.426 40.9336 253.671 40.769 253.801 40.5715C253.965 40.374 254.046 40.1271 254.046 39.8308C254.046 39.4687 253.899 39.2054 253.605 39.0408C253.344 38.8433 252.887 38.7445 252.233 38.7445C251.939 38.7445 251.58 38.7445 251.155 38.7445C250.73 38.7445 250.126 38.7445 249.342 38.7445C248.59 38.7445 247.529 38.7445 246.156 38.7445C244.98 38.7445 243.82 38.6952 242.677 38.5964C241.566 38.4977 240.553 38.2508 239.639 37.8558C238.756 37.4278 238.038 36.7695 237.482 35.8807C236.96 34.9919 236.698 33.7575 236.698 32.1775C236.698 30.3011 236.992 28.8363 237.58 27.7829C238.201 26.6967 239.083 25.8902 240.227 25.3635C241.37 24.8368 242.759 24.5076 244.392 24.376C246.026 24.2114 247.872 24.1291 249.93 24.1291C251.171 24.1291 252.478 24.1455 253.85 24.1785C255.255 24.1785 256.562 24.1949 257.771 24.2278C258.98 24.2607 259.894 24.2937 260.515 24.3266C262.051 24.3924 263.227 24.7874 264.044 25.5116C264.893 26.2358 265.318 27.141 265.318 28.2273C265.318 28.359 265.318 28.6388 265.318 29.0667C265.318 29.4617 265.318 29.8568 265.318 30.2518C265.318 30.6139 265.318 30.8114 265.318 30.8443C265.122 30.6797 264.664 30.548 263.946 30.4493C263.227 30.3505 262.377 30.2847 261.397 30.2518C260.417 30.1859 259.437 30.153 258.457 30.153C257.477 30.1201 256.611 30.1036 255.86 30.1036H250.322C249.636 30.1036 249.146 30.1695 248.852 30.3011C248.558 30.3999 248.378 30.548 248.313 30.7455C248.247 30.9101 248.215 31.0912 248.215 31.2887C248.215 31.4533 248.231 31.6179 248.264 31.7824C248.296 31.947 248.411 32.0952 248.607 32.2268C248.803 32.3256 249.113 32.375 249.538 32.375C252.053 32.375 253.997 32.375 255.369 32.375C256.742 32.375 257.493 32.375 257.624 32.375C259.323 32.375 260.776 32.5231 261.985 32.8193C263.227 33.1156 264.174 33.7575 264.828 34.745C265.514 35.6996 265.857 37.1809 265.857 39.1889C265.857 41.0323 265.514 42.5136 264.828 43.6328C264.174 44.752 263.227 45.575 261.985 46.1016C260.776 46.6283 259.355 46.941 257.722 47.0398C255.598 47.1715 253.589 47.2373 251.694 47.2373C249.832 47.2702 247.839 47.2702 245.715 47.2373C244.049 47.2044 242.612 47.1386 241.403 47.0398C240.227 46.941 239.263 46.7765 238.511 46.546C237.76 46.2827 237.205 45.9371 236.845 45.5091C236.518 45.0812 236.355 44.5216 236.355 43.8303V40.2258Z"
              fill="url(#paint4_linear_144_55)"/>
            <path
              d="M270.518 24.3266H301V30.153H291.394V43.5834C291.394 44.9989 291.166 45.9371 290.708 46.3979C290.284 46.8258 289.303 47.0398 287.768 47.0398H280.221V30.153H270.518V24.3266Z"
              fill="url(#paint5_linear_144_55)"/>
            <rect x="118" width="1" height="71" rx="0.5" fill="url(#paint6_linear_144_55)"/>
            <path
              d="M86.6043 47.0133C84.9286 47.0133 83.6637 46.767 82.8094 46.2742C81.988 45.7814 81.2323 44.8122 80.5424 43.3667L71.5728 24.345H83.943L89.2163 36.6647C89.3149 36.8947 89.4134 37.0754 89.512 37.2068C89.6106 37.3382 89.7091 37.4039 89.8077 37.4039H89.9555L95.7217 24.345H104.001L93.3068 47.0133H86.6043Z"
              fill="white"/>
            <path
              d="M52.9404 47.3583C49.8519 47.3583 47.2728 47.1612 45.2029 46.7669C43.1329 46.3398 41.4737 45.6664 40.2252 44.7465C38.9767 43.8266 38.0732 42.6275 37.5146 41.1491C36.9889 39.6707 36.7261 37.8474 36.7261 35.6791C36.7261 33.5109 36.9889 31.6875 37.5146 30.2092C38.0732 28.7308 38.9767 27.5317 40.2252 26.6118C41.4737 25.6919 43.1329 25.0349 45.2029 24.6406C47.2728 24.2135 49.8519 24 52.9404 24C56.0288 24 58.608 24.2135 60.6779 24.6406C62.7478 25.0349 64.3906 25.6919 65.6063 26.6118C66.8548 27.5317 67.7419 28.7308 68.2676 30.2092C68.8261 31.6875 69.1054 33.5109 69.1054 35.6791C69.1054 37.8474 68.8261 39.6707 68.2676 41.1491C67.7419 42.6275 66.8548 43.8266 65.6063 44.7465C64.3906 45.6664 62.7478 46.3398 60.6779 46.7669C58.608 47.1612 56.0288 47.3583 52.9404 47.3583ZM52.9404 41.6419C53.8275 41.6419 54.5667 41.5434 55.1582 41.3462C55.7496 41.1491 56.226 40.8206 56.5874 40.3607C56.9488 39.9007 57.1952 39.2929 57.3266 38.5373C57.4909 37.7489 57.573 36.7961 57.573 35.6791C57.573 34.5293 57.4909 33.5766 57.3266 32.821C57.1952 32.0653 56.9488 31.4576 56.5874 30.9976C56.226 30.5377 55.7496 30.2092 55.1582 30.012C54.5667 29.8149 53.8275 29.7164 52.9404 29.7164C52.0533 29.7164 51.2976 29.8149 50.6733 30.012C50.0819 30.2092 49.6055 30.5377 49.2441 30.9976C48.9156 31.4576 48.6691 32.0653 48.5049 32.821C48.3406 33.5766 48.2584 34.5293 48.2584 35.6791C48.2584 36.7961 48.3406 37.7489 48.5049 38.5373C48.6691 39.2929 48.9156 39.9007 49.2441 40.3607C49.6055 40.8206 50.0819 41.1491 50.6733 41.3462C51.2976 41.5434 52.0533 41.6419 52.9404 41.6419Z"
              fill="white"/>
            <path
              d="M52.9404 47.3583C49.8519 47.3583 47.2728 47.1612 45.2029 46.7669C43.1329 46.3398 41.4737 45.6664 40.2252 44.7465C38.9767 43.8266 38.0732 42.6275 37.5146 41.1491C36.9889 39.6707 36.7261 37.8474 36.7261 35.6791C36.7261 33.5109 36.9889 31.6875 37.5146 30.2092C38.0732 28.7308 38.9767 27.5317 40.2252 26.6118C41.4737 25.6919 43.1329 25.0349 45.2029 24.6406C47.2728 24.2135 49.8519 24 52.9404 24C56.0288 24 58.608 24.2135 60.6779 24.6406C62.7478 25.0349 64.3906 25.6919 65.6063 26.6118C66.8548 27.5317 67.7419 28.7308 68.2676 30.2092C68.8261 31.6875 69.1054 33.5109 69.1054 35.6791C69.1054 37.8474 68.8261 39.6707 68.2676 41.1491C67.7419 42.6275 66.8548 43.8266 65.6063 44.7465C64.3906 45.6664 62.7478 46.3398 60.6779 46.7669C58.608 47.1612 56.0288 47.3583 52.9404 47.3583ZM52.9404 41.6419C53.8275 41.6419 54.5667 41.5434 55.1582 41.3462C55.7496 41.1491 56.226 40.8206 56.5874 40.3607C56.9488 39.9007 57.1952 39.2929 57.3266 38.5373C57.4909 37.7489 57.573 36.7961 57.573 35.6791C57.573 34.5293 57.4909 33.5766 57.3266 32.821C57.1952 32.0653 56.9488 31.4576 56.5874 30.9976C56.226 30.5377 55.7496 30.2092 55.1582 30.012C54.5667 29.8149 53.8275 29.7164 52.9404 29.7164C52.0533 29.7164 51.2976 29.8149 50.6733 30.012C50.0819 30.2092 49.6055 30.5377 49.2441 30.9976C48.9156 31.4576 48.6691 32.0653 48.5049 32.821C48.3406 33.5766 48.2584 34.5293 48.2584 35.6791C48.2584 36.7961 48.3406 37.7489 48.5049 38.5373C48.6691 39.2929 48.9156 39.9007 49.2441 40.3607C49.6055 40.8206 50.0819 41.1491 50.6733 41.3462C51.2976 41.5434 52.0533 41.6419 52.9404 41.6419Z"
              fill="white"/>
            <path
              d="M31.443 35.6792C31.443 37.8803 31.1801 39.72 30.6544 41.1984C30.1616 42.6768 29.3402 43.8431 28.1902 44.6972C27.0731 45.5185 25.5782 46.1099 23.7054 46.4713C21.8655 46.8327 19.582 47.0133 16.855 47.0133C14.9494 47.0133 13.208 47.0133 11.6309 47.0133C10.0867 47.0133 8.64106 47.0133 7.29398 47.0133C5.97975 47.0133 4.7148 47.0133 3.49914 47.0133C2.31633 47.0133 1.14995 47.0133 0 47.0133V27.6959C0 26.4804 0.295703 25.6262 0.887106 25.1334C1.47851 24.6078 2.43132 24.345 3.74555 24.345C4.82979 24.345 5.91403 24.345 6.99827 24.345C8.11537 24.345 9.21604 24.345 10.3003 24.345C11.3845 24.345 12.4688 24.345 13.553 24.345C14.6701 24.345 15.7708 24.345 16.855 24.345C19.582 24.345 21.8655 24.5257 23.7054 24.887C25.5782 25.2484 27.0731 25.8562 28.1902 26.7104C29.3402 27.5317 30.1616 28.6815 30.6544 30.1599C31.1801 31.6054 31.443 33.4452 31.443 35.6792ZM11.2367 41.4941C11.5981 41.4941 11.9431 41.4941 12.2716 41.4941C12.633 41.4941 12.978 41.4941 13.3066 41.4941C13.668 41.4941 14.0294 41.4941 14.3908 41.4941C14.7522 41.4941 15.0972 41.4941 15.4258 41.4941C16.2143 41.4941 16.8879 41.412 17.4464 41.2477C18.0378 41.0506 18.5142 40.7385 18.8756 40.3114C19.237 39.8515 19.4999 39.2437 19.6642 38.4881C19.8284 37.7325 19.9106 36.7962 19.9106 35.6792C19.9106 34.0365 19.7463 32.8045 19.4178 31.9832C19.0892 31.1619 18.5635 30.6034 17.8407 30.3077C17.1507 30.0121 16.1979 29.8642 14.9822 29.8642C14.588 29.8642 14.1773 29.8642 13.7501 29.8642C13.323 29.8642 12.8959 29.8642 12.4688 29.8642C12.0745 29.8642 11.6638 29.8642 11.2367 29.8642C11.2367 30.0942 11.2367 30.3406 11.2367 30.6034C11.2367 30.8334 11.2367 31.0798 11.2367 31.3426V41.4941Z"
              fill="white"/>
            <defs>
              <linearGradient id="paint0_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint1_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint2_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint3_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint4_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint5_linear_144_55" x1="113" y1="35" x2="301" y2="35"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00"/>
                <stop offset="0.5" stopColor="#20E100"/>
                <stop offset="1" stopColor="#1FDE00"/>
              </linearGradient>
              <linearGradient id="paint6_linear_144_55" x1="118.5" y1="0" x2="118.5" y2="71"
                              gradientUnits="userSpaceOnUse">
                <stop stopColor="#1FDE00" stopOpacity="0"/>
                <stop offset="0.192708" stopColor="#1FDE00"/>
                <stop offset="0.807292" stopColor="#1FDE00"/>
                <stop offset="1" stopColor="#1FDE00" stopOpacity="0"/>
              </linearGradient>
            </defs>
          </svg>
        </NavLink>
      </StyledLogoWrapper>

      <StyledActions>

        {
          isAuthorized && isAdmin && (
            <Button as={StyledRefreshButton} loading={isRefreshingAllInvestorsData} variant="white" small
                    onClick={refreshAllInvestorsData}>
              <StyledDesktopOnlyElement>
                {t('refreshAllInvestorsData')}
              </StyledDesktopOnlyElement>

              <StyledMobileOnlyElement>
                <RefreshIcon/>
              </StyledMobileOnlyElement>
            </Button>
          )
        }

        {!isAuthorized && !isLogin &&
          <Button as={StyledAuthorizeButton} to={ROUTE.login} variant="white" small>
            <StyledDesktopOnlyElement>
              {t('loginButton')}
            </StyledDesktopOnlyElement>

            <StyledMobileOnlyElement>
              <LogInIcon/>
            </StyledMobileOnlyElement>
          </Button>}

        {
          isAuthorized && isHome && (
            <Button as={StyledAuthorizeButton} to={isAdmin ? DEFAULT_ADMIN_ROUTE : DEFAULT_INVESTOR_ROUTE} variant="white"
                    small>
              <StyledDesktopOnlyElement>
                {t('cabinet')}
              </StyledDesktopOnlyElement>

              <StyledMobileOnlyElement>
                <CabinetIcon/>
              </StyledMobileOnlyElement>
            </Button>
          )
        }

        <IconButtonSelect icon={LANG_CODE_NAME_PATCHES[i18n.resolvedLanguage] || i18n.resolvedLanguage}>
          {LANGUAGES.map((lng) => (
            <MenuItem key={lng} active={lng === i18n.resolvedLanguage} onClick={() => i18n.changeLanguage(lng)}>
              {LANGUAGE_TO_NAME[lng]}
            </MenuItem>
          ))}
        </IconButtonSelect>

        {
          isAuthorized && !isHome && (
            <IconButtonSelect icon={<CabinetIcon/>}>
              <MenuItem as="a" href={CHAT_WITH_MANAGER_LINK_ENV} target="_blank" icon={<ChatTgIcon/>}>
                {t('chatWithManager')}
              </MenuItem>
              <HorizontalDivider/>
              <MenuItem icon={<LogOutIcon/>} onClick={logout}>
                {t('logout')}
              </MenuItem>
            </IconButtonSelect>
          )
        }

      </StyledActions>
    </StyledRoot>
  )
}